import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Sparkles, Pencil } from 'lucide-react';
import { pluralCredits, type ProfileResponse } from '@masha/shared';
import { api, ApiError, type SessionRewriteResponse } from '../lib/api';
import { paidRewriteLabel, usePricing } from '../lib/pricing';
import { Button, Modal, Textarea, Toast } from './ui';

const MIN_LEN = 3;
const MAX_LEN = 500;

interface RewriteModalProps {
  sessionId: number;
  rewriteCost: 'free' | 'paid' | 'blocked';
  initialFeedback: string;
  onSubmitted: (resp: SessionRewriteResponse) => void;
  onClose: () => void;
  onEditAnswers: (currentFeedback: string) => void;
}

export function RewriteModal({
  sessionId,
  rewriteCost,
  initialFeedback,
  onSubmitted,
  onClose,
  onEditAnswers,
}: RewriteModalProps) {
  const [feedback, setFeedback] = useState(initialFeedback);
  const navigate = useNavigate();
  const pricing = usePricing();
  const turboUntil =
    useQuery<ProfileResponse>({ queryKey: ['profile'] }).data?.user.turboUntil ?? null;

  function goToBalance() {
    onClose();
    navigate('/balance');
  }

  const [error, setError] = useState<string | null>(null);
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);

  const trimmed = feedback.trim();
  const tooShort = trimmed.length < MIN_LEN;
  const tooLong = feedback.length > MAX_LEN;
  const validLength = !tooShort && !tooLong;
  const canSubmit = validLength && rewriteCost !== 'blocked';

  const mutation = useMutation({
    mutationFn: (body: { feedback: string }) => api.rewriteSession(sessionId, body),
    onSuccess: (resp) => {
      setError(null);
      setShowInsufficientCredits(false);
      onSubmitted(resp);
    },
    onError: (err) => {
      setError(null);
      setShowInsufficientCredits(false);
      if (err instanceof ApiError) {
        if (err.status === 402) {
          setShowInsufficientCredits(true);
          return;
        }
        if (err.status === 400 && err.code === 'INVALID_BODY') {
          setError('Минимум 3 и максимум 500 символов.');
          return;
        }
      }
      setError('Не удалось переписать. Попробуйте ещё раз.');
    },
  });

  const submitLabel =
    rewriteCost === 'free'
      ? 'Переписать (бесплатно)'
      : rewriteCost === 'paid'
        ? paidRewriteLabel(pricing.rewrite, turboUntil)
        : 'Переписать';

  function handleSubmit() {
    if (!canSubmit) return;
    setError(null);
    setShowInsufficientCredits(false);
    mutation.mutate({ feedback: trimmed });
  }

  function handleEditAnswers() {
    onEditAnswers(feedback);
  }

  return (
    <Modal open onClose={onClose} title="Что переписать?">
      {rewriteCost === 'paid' && (
        <Toast tone="warning" className="mb-3">
          {turboUntil && new Date(turboUntil).getTime() > Date.now()
            ? 'Турбо: переделка без списания кредитов.'
            : `Это платная перегенерация. Спишется ${pricing.rewrite} ${pluralCredits(pricing.rewrite)}.`}
        </Toast>
      )}
      {rewriteCost === 'blocked' && (
        <Toast tone="destructive" className="mb-3">
          Бесплатная перегенерация использована.{' '}
          <button type="button" onClick={goToBalance} className="underline font-medium">
            Пополнить баланс
          </button>
          .
        </Toast>
      )}

      <Textarea
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        placeholder="Например: «Слишком формально, добавь сленга и пример из жизни»"
        rows={5}
        maxLength={MAX_LEN}
        autoFocus
        disabled={mutation.isPending}
        invalid={tooLong}
      />
      <div className="flex justify-between text-xs text-tg-hint mt-1 mb-3">
        <span>
          {tooShort && trimmed.length > 0 ? 'Минимум 3 символа' : ''}
          {tooLong ? 'Превышен лимит' : ''}
        </span>
        <span className={tooLong ? 'text-destructive' : ''}>
          {feedback.length}/{MAX_LEN}
        </span>
      </div>

      {showInsufficientCredits && (
        <Toast tone="destructive" className="mb-3">
          Не хватает кредитов.{' '}
          <button type="button" onClick={goToBalance} className="underline font-medium">
            Пополнить
          </button>
          .
        </Toast>
      )}

      {error && (
        <Toast tone="destructive" className="mb-3">
          {error}
        </Toast>
      )}

      <Button
        onClick={handleSubmit}
        disabled={!canSubmit || mutation.isPending}
        loading={mutation.isPending}
        variant="primary"
        size="lg"
        fullWidth
        leftIcon={!mutation.isPending ? <Sparkles size={18} /> : undefined}
        className="mb-2"
      >
        {mutation.isPending ? 'Переписываем' : submitLabel}
      </Button>

      <Button
        onClick={handleEditAnswers}
        disabled={mutation.isPending}
        variant="secondary"
        size="lg"
        fullWidth
        leftIcon={<Pencil size={16} />}
        className="mb-2"
        hapticKind="light"
      >
        Изменить ответы
      </Button>

      <Button
        onClick={onClose}
        disabled={mutation.isPending}
        variant="ghost"
        size="sm"
        fullWidth
        hapticKind={false}
        className="text-tg-hint"
      >
        Отмена
      </Button>
    </Modal>
  );
}
